import { useMemo } from 'react';

import { Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { TipoQuantidadeMaximaProdutoAutoComplete } from 'components/autocompletes';
import { BbarForm } from 'components/docked/BbarForm';
import { CissNumberField } from 'components/fields';
import { Row } from 'components/form';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useClientContext } from 'context/ClientContext';
import { useLoaderContext } from 'context/LoaderContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import { useConfigPedidoDirty, useMutateRegrasValidacao, useQueryRegrasValidacao } from 'pages/compra/configuracao-pedido/hooks';
import { initialRegrasValidacaoValues } from 'pages/compra/configuracao-pedido/types';

export function ConfigPedidoRegrasValidacaoForm() {
    const { data, isFetching } = useQueryRegrasValidacao();
    const { onSubmit, isLoading } = useMutateRegrasValidacao();
    const { focusError } = useFocusErrorFormik();
    const { tipoPedido } = useClientContext();

    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';
    const isTipoPedidoPadrao = tipoPedido === 'padrao';

    useLoaderContext(isLoading || isFetching);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                peOferta: Yup.number().max(100),
            }),
        [],
    );

    const formik = useFormik({
        initialValues: data || initialRegrasValidacaoValues,
        onSubmit,
        validationSchema,
        enableReinitialize: true,
    });

    const { values, errors, setFieldValue, submitForm, dirty } = formik;

    focusError(errors);

    useConfigPedidoDirty(dirty);

    return (
        <FormikProvider value={formik}>
            <Block>
                <SubTitle label="Regras" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissNumberField
                            label="Percentual de oferta"
                            name="peOferta"
                            size="small"
                            inputType="decimal"
                            maxValue={100}
                            disabled={values.fgUtilizaDescontoCadastroLoja}
                            value={values.peOferta || 0}
                            onChange={(e) => setFieldValue('peOferta', Number(e.target.value))}
                        />
                    </Grid>

                    {isTipoPedidoFuncionario && (
                        <Grid sx={{ flex: 1 }}>
                            <CissNumberField
                                label="Valor base para frete grátis"
                                name="vlBaseFreteGratis"
                                inputType="decimal"
                                size="small"
                                value={values.vlBaseFreteGratis}
                                disabled={!values.fgUtilizaVlBaseFreteGratis}
                                onChange={(e) => setFieldValue('vlBaseFreteGratis', Number(e.target.value))}
                            />
                        </Grid>
                    )}
                </Row>
            </Block>

            <Block>
                <SubTitle label="Habilitar regras" />

                <FormGroup>
                    {isTipoPedidoFuncionario && (
                        <FormControlLabel
                            label="Utilizar valor base para frete grátis"
                            name="fgUtilizaVlBaseFreteGratis"
                            control={<Checkbox />}
                            checked={values.fgUtilizaVlBaseFreteGratis || false}
                            onChange={(e, checked) => {
                                setFieldValue('fgUtilizaVlBaseFreteGratis', checked);
                                setFieldValue('vlBaseFreteGratis', 0);
                            }}
                        />
                    )}

                    <FormControlLabel
                        label="Utilizar desconto do cadastro de loja"
                        name="fgUtilizaDescontoCadastroLoja"
                        control={<Checkbox />}
                        checked={values.fgUtilizaDescontoCadastroLoja || false}
                        onChange={(e, checked) => {
                            setFieldValue('fgUtilizaDescontoCadastroLoja', checked);
                            setFieldValue('peOferta', 0);
                        }}
                    />

                    <FormControlLabel
                        label="Validar quantidade mínima e quantidade do múltiplo do produto"
                        name="fgValidaQuantidadeMinimaMultiploProduto"
                        control={<Checkbox />}
                        checked={values.fgValidaQuantidadeMinimaMultiploProduto || false}
                        onChange={(e, checked) => setFieldValue('fgValidaQuantidadeMinimaMultiploProduto', checked)}
                    />

                    <FormControlLabel
                        label="Exibir sugestão por produto"
                        name="fgExibeSugestaoProduto"
                        control={<Checkbox />}
                        checked={values.fgExibeSugestaoProduto || false}
                        onChange={(e, checked) => setFieldValue('fgExibeSugestaoProduto', checked)}
                    />

                    <FormControlLabel
                        label="Requer aprovação do pedido"
                        name="fgRequerAprovacaoPedido"
                        control={<Checkbox />}
                        checked={values.fgRequerAprovacaoPedido || false}
                        onChange={(e, checked) => setFieldValue('fgRequerAprovacaoPedido', checked)}
                    />

                    {isTipoPedidoPadrao && (
                        <FormControlLabel
                            label="Validar inadimplência"
                            name="fgValidaInadimplencia"
                            control={<Checkbox />}
                            checked={values.fgValidaInadimplencia || false}
                            onChange={(e, checked) => setFieldValue('fgValidaInadimplencia', checked)}
                        />
                    )}
                </FormGroup>

                <TipoQuantidadeMaximaProdutoAutoComplete
                    sx={{ width: { md: '50%' }, paddingTop: 1 }}
                    name="tipoControleQuantidadeMaximaProduto"
                    error={errors.tipoControleQuantidadeMaximaProduto}
                    value={values.tipoControleQuantidadeMaximaProduto}
                    onChange={(e, value) => setFieldValue('tipoControleQuantidadeMaximaProduto', value)}
                />
            </Block>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}